import React from "react";
import FemaleProvider from "../assets/Doctor_Female.jpg";
import MaleProvider from "../assets/Doctor_Male_96.png";
import styles from "../modals/ResourceViewerModal.module.css";
import { calculateAge } from "../utils/dateUtils";
import { getResourceIcon} from "../utils/renderUtils";

const ProviderCardExtended = ({ resource }) => {
  const name = resource.name?.[0];
  const address = resource.address?.[0];
  const geo = address?.extension?.find(e => e.url.includes("geolocation"))?.extension || [];
  const age = calculateAge(resource.birthDate);
  const gender = resource.gender?.toLowerCase() ?? 'unknown';

  // Photo fallback
  const photoUrl = resource.photo?.[0]?.url || (gender === 'female' ? FemaleProvider : MaleProvider);
  const [imgSrc, setImgSrc] = React.useState(photoUrl);

  // Collect all identifiers (top-level + qualification identifiers)
  const allIdentifiers = [
    ...(resource.identifier || []),
    ...resource.qualification?.flatMap(q => q.identifier || []) || []
  ];

  // Qualifications with issuer
  const qualifications = resource.qualification?.length
    ? resource.qualification.map((q) => {
      const code = q.code?.text || q.code?.coding?.[0]?.display || "Qualification";
      const issuer = q.issuer?.display || "";
      return `${code}${issuer ? ` (${issuer})` : ""}`;
    })
    : ["Unknown"];

  return (
    <div className={styles.card}>
      {/* Header */}
      <div className={`${styles.header} mb-4`}>
        <div className={styles.photoContainer}>
          <img
            src={imgSrc}
            alt="Provider Portrait"
            onError={() =>
              setImgSrc(gender === "female" ? FemaleProvider : MaleProvider)
            }
            className={styles.photo}
          />
        </div>
        <div>
          <h3 className="mb-1">
            {name?.prefix?.join(" ")} {name?.given?.join(" ")} {name?.family}
          </h3>
          <div className="mb-1 text-muted"><strong>Gender:</strong> {gender}</div>
          <div className="mb-1 text-muted"><strong>Active:</strong> {resource.active ? "Yes" : "No"}</div>
          <div className="mb-1 text-muted">
            <strong>Birth Date:</strong> {resource.birthDate || "Unknown"}
            {age !== "Unknown" && ` (Age: ${age})`}
          </div>


        </div>
      </div>

      {/* Section: Identifiers */}
      <div className={styles.sectionHeader}>Identifiers</div>
      <div className={styles.indented}>
        <div className={styles.sectionContent}>
          {allIdentifiers.length > 0 ? (
            allIdentifiers.map((id, idx) => (

              <div key={idx} className="mb-1">
                <div className="mb-1 text-muted"><strong>Provider ID:</strong> {resource.id}</div>
                <strong>{id.system?.includes("us-npi") ? "NPI" : "Identifier"}:</strong> {id.value}
              </div>
            ))
          ) : (
            <div className="mb-1">No identifiers available</div>
          )}
        </div>
      </div>

      {/* Section: Telecom */}
      <div className={styles.sectionHeader}>Contact</div>
      <div className={styles.indented}>
        <div className={styles.sectionContent}>
          {resource.telecom?.map((t, idx) => (
            <div key={idx} className="mb-1">
              <strong>{t.system?.charAt(0).toUpperCase() + t.system?.slice(1)} ({t.use || "work"}):</strong> {t.value}
            </div>
          ))}
        </div></div>

      {/* Section: Location */}
      <div className={styles.sectionHeader}>Location</div>
      <div className={styles.indented}>
        <div className={styles.sectionContent}>
          {address ? (
            <>
              <div className="mb-1">
                <strong>Address:</strong>{" "}
                {address.line?.join(", ")}, {address.city}, {address.state} {address.postalCode}, {address.country}
                {geo.length > 0 && (
                  <span className="text-muted">
                    {" "} (Lat: {geo.find(e => e.url === "latitude")?.valueDecimal}, Lon: {geo.find(e => e.url === "longitude")?.valueDecimal})
                  </span>
                )}
              </div>
              <div className="mb-1">
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    `${address.line?.join(", ")}, ${address.city}, ${address.state} ${address.postalCode}, ${address.country}`
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View on Google Maps
                </a>
              </div>
            </>
          ) : (
            <div className="mb-1">No address available</div>
          )}
        </div>
      </div>

      {/* Section: Qualifications */}
      <div className={styles.sectionHeader}>Qualifications</div>
      <div className={styles.indented}>
        {qualifications.map((q, idx) => (
          <div key={idx} className="mb-1">
            <strong>Qualification:</strong> {q}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProviderCardExtended;
